<template>
  <div>
    <v-dialog
      v-model="dialog"
      :min-width="modalSpecial.FILE_DETAIL_MIN_WIDTH"
      v-bind="modalPolicy.P2_FORM"
      :data-testid="getTestId('file-detail-dialog')"
      @keydown.esc="handleEsc"
      @click:outside="closeDialog"
      @close="closeDialog"
    >
      <v-card style="min-height: 90vh">
        <CrudFormDialogHeader
          :kicker="$t('common.details')"
          :title="dialogTitle"
          show-close
          @close="closeDialog"
        />
        <v-tabs v-model="activeTab">
          <v-tab>{{ $t('imageTypes.rgb') }}</v-tab>
          <v-tab>{{ $t('imageTypes.ir') }}</v-tab>
          <v-tab>{{ $t('imageTypes.csv') }}</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <v-tab-item
            v-for="resource in [ResourceTypes.RGB, ResourceTypes.IR]"
            :key="resource"
            :transition="false"
            class="dynamic_height"
          >
            <v-card style="height: 100%; width: 100%">
              <div v-if="!loading[resource]" style="height: 100%; width: 100%">
                <div
                  style="
                    display: flex;
                    gap: 10px;
                    width: 100%;
                    padding-top: 10px;
                    padding-left: 10px;
                    padding-right: 10px;
                    align-items: center;
                    position: relative;
                  "
                >
                  <v-btn
                    v-if="isSelectMode[resource]"
                    outlined
                    class="solvit-crud-outline-btn"
                    @click="toggleSelectMode(resource)"
                  >
                    <v-icon>mdi-checkbox-marked-outline</v-icon>{{ $t('fileDetail.selectModeLabel') }}
                  </v-btn>
                  <v-btn
                    v-else
                    outlined
                    class="solvit-crud-outline-btn"
                    @click="toggleSelectMode(resource)"
                  >
                    <v-icon>mdi-eye-outline</v-icon>{{ $t('fileDetail.viewModeLabel') }}
                  </v-btn>
                  <v-divider vertical />
                  <template v-if="isSelectMode[resource]">
                    <v-btn outlined class="solvit-crud-outline-btn" @click="deselectAll(resource)">
                      {{ $t('fileDetail.deselectAll') }}
                    </v-btn>
                    <div
                      style="
                        display: flex;
                        font-size: 14px;
                        font-weight: 500;
                        min-width: 50px;
                        margin-left: 10px;
                        color: var(--v-info-base);
                        align-items: center;
                      "
                    >
                      {{ $t('fileDetail.selectedCount', { count: localSelectedImages[resource]?.length || 0 }) }}
                    </div>
                  </template>
                  <v-divider style="border: none" />
                  <v-btn
                    v-if="isSelectMode[resource]"
                    outlined
                    class="solvit-crud-outline-btn solvit-crud-outline-btn--danger"
                    @click="deleteSelectedFiles(resource)"
                  >
                    {{ $t('fileDetail.deleteSelected') }}
                  </v-btn>
                  <v-btn
                    v-if="isSelectMode[resource] && !isProductionMode"
                    outlined
                    class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                    @click="downloadSelectedImages(resource)"
                  >
                    <v-icon>mdi-download</v-icon>{{ $t('fileDetail.downloadSelected') }}
                  </v-btn>
                  <v-btn
                    v-if="!isProductionMode"
                    outlined
                    class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                    @click="downloadAllImages(resource)"
                  >
                    <v-icon>mdi-download</v-icon>{{ $t('fileDetail.downloadAll') }}
                  </v-btn>
                  <v-btn
                    outlined
                    class="solvit-crud-outline-btn solvit-crud-outline-btn--danger"
                    style="margin-right: 20px"
                    @click="deleteAllFiles(resource)"
                  >
                    {{ $t('fileDetail.deleteAll') }}
                  </v-btn>
                </div>
                <div
                  class="mt-3 ml-3"
                  style="height: 30px;"
                >
                  <span
                    v-if="hoveredFileName[resource]"
                    class="text-truncate rounded pa-2 hovered-file-name"
                    style="font-size: 13px; max-width: 100%;"
                  >
                    {{ hoveredFileName[resource] }}
                  </span>
                  <span v-else class="invisible" style="height: 0;">&nbsp;</span>
                </div>
                <div v-if="resourceImages(resource)" class="gallery" :data-testid="getTestId('uploaded-files-list')">
                  <div
                    v-for="image in resourceImages(resource)"
                    :key="getKey(resource, image)"
                  >
                    <div
                      class="image-container"
                      @mouseover="handleImageHover(resource, image)"
                      @mouseleave="handleImageLeave(resource)"
                      @click="selectImage(resource, image)"
                    >
                      <v-img
                        width="100%"
                        height="100%"
                        :src="image?.thumbImgUrl"
                        contain
                      />
                      <div v-if="isSelectMode[resource]" class="overlay">
                        <v-icon
                          v-if="isSelected(resource, getKey(resource, image))"
                        >
                          mdi-check-circle-outline
                        </v-icon>
                        <v-icon
                          v-else
                          class="showIcon_hover_selected"
                        >
                          mdi-check-circle-outline
                        </v-icon>
                      </div>
                      <div class="file-name">
                        {{ ellipsisString(image?.imgName, { type: 'length', frontLength: 4, backLength: 16 }) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="!resourceImages(resource).length > 0"
                    style="
                      width: 100%;
                      height: 100%;
                      display: flex;
                      flex-direction: column;
                      gap: 10px;
                      align-items: center;
                    "
                  >
                    <div>{{ $t('fileDetail.noImages') }}</div>
                    <v-btn outlined class="solvit-crud-outline-btn solvit-crud-outline-btn--primary" @click="initImages(resource)">
                      {{ $t('fileDetail.refresh') }}
                    </v-btn>
                  </div>
                </div>
              </div>
              <div v-else style="height: 100%; width: 100%">
                <div
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                  "
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="100"
                  />
                </div>
              </div>
            </v-card>
          </v-tab-item>
          <v-tab-item
            v-if="!loading[ResourceTypes.CSV]"
            :key="ResourceTypes.CSV"
            :transition="false"
          >
            <v-card>
              <div
                style="
                  display: flex;
                  gap: 10px;
                  width: 100%;
                  padding-top: 10px;
                  padding-left: 10px;
                "
              >
                <template>
                  <div
                    style="
                      display: flex;
                      font-size: 14px;
                      font-weight: 500;
                      min-width: 50px;
                      margin-left: 10px;
                      color: var(--v-info-base);
                      align-items: center;
                    "
                  >
                    {{ $t('fileDetail.selectedCount', { count: Object.values(checkedRows).length }) }}
                  </div>
                </template>
                <v-divider style="border: none" />
                <v-btn outlined class="solvit-crud-outline-btn solvit-crud-outline-btn--danger" @click="deleteSelectedFiles(ResourceTypes.CSV)">
                  {{ $t('fileDetail.deleteSelected') }}
                </v-btn>
                <v-btn
                  outlined
                  class="solvit-crud-outline-btn solvit-crud-outline-btn--danger"
                  style="margin-right: 20px"
                  @click="deleteAllFiles(ResourceTypes.CSV)"
                >
                  {{ $t('fileDetail.deleteAll') }}
                </v-btn>
              </div>
              <div v-if="csvFiles" class="table">
                <v-data-table
                  v-model="checkedRows"
                  :headers="csvHeaders"
                  :items="csvFiles"
                  sort-by="seq"
                  :sort-desc="[true]"
                  :items-per-page="itemsPerPage"
                  show-select
                  hide-default-footer
                  :loading="apiLoading"
                  :loading-text="$t('fileDetail.loadingData')"
                >
                  <template
                    #headers="{ columns, getSortIcon, toggleSort }"
                  >
                    <tr v-for="column in columns" :key="column.key">
                      <td>
                        <span
                          class="mr-2 cursor-pointer"
                          @click="() => toggleSort(column)"
                        >{{ column.text }}</span>
                        <template v-if="isSorted(column)">
                          <v-icon :icon="getSortIcon(column)" />
                        </template>
                      </td>
                    </tr>
                  </template>
                  <template slot="item.filename" slot-scope="{ item }">
                    <div style="display: flex; align-items: center">
                      {{ item.filename }}
                    </div>
                  </template>
                  <template slot="item.fileUrl" slot-scope="{ item }">
                    {{ item.fileUrl }}
                  </template>
                  <template slot="item.filelink" slot-scope="{ item }">
                    <a :href="item.fileUrl">{{ $t('common.download') }}</a>
                  </template>
                  <template slot="item.actions" slot-scope="{ item }">
                    <v-btn
                      :key="item.id"
                      outlined
                      class="solvit-crud-outline-btn solvit-crud-outline-btn--danger"
                      @click="deleteSelectedFiles(ResourceTypes.CSV, item)"
                    >
                      <v-icon icon="$close" />{{ $t('common.delete') }}
                    </v-btn>
                  </template>
                  <template slot="no-data">
                    <div style="margin: 10px">
                      {{ $t('fileDetail.noCsvFiles') }}
                    </div>
                    <v-btn outlined class="solvit-crud-outline-btn solvit-crud-outline-btn--primary" @click="initCSVFiles">
                      {{ $t('fileDetail.refresh') }}
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </v-card>
            <div class="text-center pt-2">
              <v-pagination
                v-model="page"
                :length="pageLength"
                :total-visible="10"
                next-icon="mdi-chevron-right"
                prev-icon="mdi-chevron-left"
                @next="next"
                @previous="previous"
                @input="paginationInput"
              />
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="ImageDialog"
      :min-width="modalSpecial.IMAGE_PREVIEW_MIN_WIDTH"
      v-bind="modalPolicy.P1_DISMISSIBLE"
      height="100vh"
      style="z-index: 300"
    >
      <v-card class="image-preview-dialog-card" style="height: 100vh">
        <CrudFormDialogHeader
          :kicker="$t('common.images')"
          :title="(ImageDialogItem && ImageDialogItem.imgName) || '—'"
          show-close
          @close="closeImageDialog"
        />
        <div v-if="ImageDialogItem" class="fullGallery">
          <v-img
            height="100%"
            :src="ImageDialogItem.imgUrl"
            alt="NoImage"
            contain
          />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { getTestId } from '@/utils/testUtils';
import { ellipsisString } from '@/utils/displayUtils';
import {
  downloadImagesAsZip,
  generateFolderNameFromReport,
  splitImagesByMaxBytes,
  MAX_ZIP_DOWNLOAD_BYTES
} from '@/utils/fileDownloadUtils';
import { normalizeReportInfo } from '@/utils/reportUtils';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_LAYOUT_SPECIAL, MODAL_CLOSE_POLICY } from '@/constants/modalLayout';

export const ResourceTypes = {
  RGB: 'RGB',
  IR: 'IR',
  CSV: 'CSV'
};

export default {
  name: 'FileDetailDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    reportId: {
      type: Number,
      required: true
    },
    dialog: {
      type: Boolean,
      required: true
    },
    mode: {
      type: String,
      required: true
    },
    editedItem: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      modalSpecial: MODAL_LAYOUT_SPECIAL,
      modalPolicy: MODAL_CLOSE_POLICY,
      apiLoading: true,
      ImageDialog: false,
      ImageDialogItem: {},
      activeTab: 0,
      snackbar: false,
      snackbarCloseBtn: false,
      resourceType: ResourceTypes.RGB,
      ResourceTypes: ResourceTypes,
      isSelectMode: {
        [ResourceTypes.RGB]: false,
        [ResourceTypes.IR]: false,
        [ResourceTypes.CSV]: true
      },
      loading: {
        [ResourceTypes.RGB]: false,
        [ResourceTypes.IR]: false,
        [ResourceTypes.CSV]: false
      },
      images: {
        [ResourceTypes.RGB]: [],
        [ResourceTypes.IR]: []
      },
      localSelectedImages: {
        [ResourceTypes.RGB]: [],
        [ResourceTypes.IR]: []
      },
      hoveredFileName: {
        [ResourceTypes.RGB]: null,
        [ResourceTypes.IR]: null
      },
      hover: null,
      csvFiles: [],
      page: 1,
      pageLength: 0,
      itemsPerPage: 10,
      checkedRows: [],
      allChecked: false
    };
  },
  computed: {
    ...mapGetters({
      resourceImages: 'uploadedfile/resourceImages',
      selectMode: 'uploadedfile/selectMode',
      selectedImages: 'uploadedfile/selectedImages'
    }),
    isProductionMode() {
      return process.env.NODE_ENV === 'production';
    },
    reportData() {
      // editedItem에서 보고서 데이터 가져오기
      return this.$props.editedItem || null;
    },
    siteName() {
      if (!this.reportData) {
        return '';
      }
      // normalizeReportInfo를 사용하여 사이트명 추출 (pagination API와 동일한 방식)
      const isApiResponse = this.reportData._embedded !== undefined;
      const reportInfo = normalizeReportInfo(this.reportData, isApiResponse ? 'api' : 'table');
      return reportInfo.siteName || '';
    },
    csvHeaders() {
      return [
        { text: this.$t('files.csvFileName'), value: 'filename' },
        { text: this.$t('fileDetail.columnFileUrl'), value: 'fileUrl' },
        { text: this.$t('files.fileLink'), value: 'filelink', sortable: false },
        { text: this.$t('common.delete'), value: 'actions', sortable: false }
      ];
    },
    dialogTitle() {
      const baseTitle = (() => {
        switch (this.$props.mode) {
        case 'upload':
          return this.$t('fileDetail.modeUpload');
        case 'pairing':
          return this.$t('fileDetail.modePairing');
        case 'defect':
          return this.$t('fileDetail.modeDefect');
        case 'detail':
          return this.$t('fileDetail.modeDetail');
        default:
          return this.$t('fileDetail.modeDefault');
        }
      })();
      
      // 사이트명이 있으면 타이틀에 추가
      if (this.siteName) {
        return `${baseTitle} - ${this.siteName}`;
      }
      
      return baseTitle;
    }
  },
  watch: {
    resourceImages(images) {
    },
    dialog: {
      handler(newValue) {
        if (this.$props.dialog) {
          this.initImages(ResourceTypes.RGB);
        }
      },
      deep: true
    },
    checkedRows(newValue) {
    },
    activeTab(newValue) {
      if (newValue == null || newValue == undefined) return;
      if (newValue == 0) this.resourceType = ResourceTypes.RGB;
      if (newValue == 1) this.resourceType = ResourceTypes.IR;
      if (newValue == 2) this.resourceType = ResourceTypes.CSV;

      console.log(
        '테스트 lengths:',
        this.images[ResourceTypes.RGB].length,
        this.images[ResourceTypes.IR].length,
        this.csvFiles.length,
        newValue
      );
      if (newValue == 0 && this.images[ResourceTypes.RGB].length == 0) {
        this.initImages(ResourceTypes.RGB);
      } else if (newValue == 1 && this.images[ResourceTypes.IR].length == 0) {
        this.initImages(ResourceTypes.IR);
      } else if (newValue == 2 && this.csvFiles.length === 0) {
        this.initImages(ResourceTypes.CSV);
      }
    },
    init(newValue) {
    },
    SelectedFiles(newValue) {
    },
    allChecked(newValue) {
      this.toggleAllCheckboxes();
    }
  },
  methods: {
    getTestId(testId) {
      return getTestId(testId);
    },
    ...mapActions({
      clearSelectedImages: 'uploadedfile/clearSelectedImages',
      clearSelectedFiles: 'uploadedfile/clearSelectedFiles',
      clearStates: 'uploadedfile/clearStates',
      init: 'uploadedfile/init',
      clearAllFiles: 'uploadedfile/clearAllFiles'
    }),
    onDialogInit() {
      this.initImages(ResourceTypes.RGB);
    },
    closeDialog() {
      this.clearStates();
      this.resetData();
      this.$emit('close');
    },
    getKey(resource, image) {
      if (resource == ResourceTypes.IR) {
        return image.seq;
      } else return image.id;
    },
    closeImageDialog() {
      this.ImageDialog = false;
    },
    toggleSelectMode(resourceType) {
      this.$set(
        this.isSelectMode,
        resourceType,
        !this.isSelectMode[resourceType]
      );
      this.$set(this.localSelectedImages, resourceType, []);
    },
    toggleSelection(resourceType, imageKey) {
      if (this.localSelectedImages[resourceType].includes(imageKey)) {
        this.$set(
          this.localSelectedImages,
          resourceType,
          this.localSelectedImages[resourceType].filter((id) => id != imageKey)
        );
      } else {
        this.localSelectedImages[resourceType].push(imageKey);
      }
    },
    /**
     *
     * @param resourceType
     * @param selectedFile File 단일삭제일때 해당 파일
     */
    async deleteSelectedFiles(resourceType, selectedFile) {
      let kind;
      let selectedArr;
      const multiple = !selectedFile?.id;
      console.log(
        '테스트 deleteSelectedFiles:',
        resourceType,
        multiple,
        selectedFile?.id,
        resourceType === ResourceTypes.CSV
      );
      if (resourceType === ResourceTypes.CSV) {
        kind = 'csv';
        selectedArr = this.checkedRows.map((row) => row.id);
        if (multiple && (!selectedArr || selectedArr.length == 0)) {
          alert(this.$t('fileDetail.noSelectedFiles'));
          return;
        }
      }

      if (
        resourceType === ResourceTypes.IR ||
        resourceType === ResourceTypes.RGB
      ) {
        kind = 'image';
        selectedArr = this.localSelectedImages[resourceType];
        if (multiple && (!selectedArr || selectedArr.length == 0)) {
          alert(this.$t('fileDetail.noSelectedImages'));
          return;
        }
      }
      const confirmSingle =
        kind === 'image'
          ? this.$t('fileDetail.confirmDeleteSingleImage')
          : this.$t('fileDetail.confirmDeleteSingleFile');
      const confirmMany =
        kind === 'image'
          ? this.$t('fileDetail.confirmDeleteManyImages', {
            count: selectedArr?.length || 0
          })
          : this.$t('fileDetail.confirmDeleteManyFiles', {
            count: selectedArr?.length || 0
          });
      if (
        (!multiple && confirm(confirmSingle)) ||
        (multiple && confirm(confirmMany))
      ) {
        try {
          const tempSelectedArr = multiple ? selectedArr : [selectedFile?.id];
          console.log(
            '테스트 selectedArr:',
            resourceType,
            selectedArr,
            tempSelectedArr
          );
          const failedKeys = await this.clearSelectedImages({
            resourceType,
            selectedArr: tempSelectedArr
          });
          Promise.all(failedKeys);

          if (!failedKeys) {
            if (kind === 'image') {
              await this.initImages(resourceType);
              this.$set(this.localSelectedImages, resourceType, []);
            } else {
              await this.initCSVFiles();
              this.checkedRows = [];
            }
            return;
          }

          if (kind === 'image') {
            const failedImages = this.images[resourceType].filter((image) =>
              failedKeys.includes(this.getKey(resourceType, image))
            );
            alert(
              this.$t('fileDetail.deleteFailedImages', {
                names: failedImages.map((image) => image.imgName).join(', ')
              })
            );
          }
          if (kind === 'csv') {
            const failedCsvRows = this.csvFiles.filter((file) =>
              failedKeys.includes(file.seq)
            );
            alert(
              this.$t('fileDetail.deleteFailedCsv', {
                names: failedCsvRows.map((f) => f.filename).join(', ')
              })
            );
          }
        } catch (error) {
          alert(this.$t('fileDetail.deleteError'));
        }
      }
    },
    async deleteAllFiles(resourceType) {
      const reportId = this.$props.reportId;
      switch (resourceType) {
      case ResourceTypes.RGB:
        if (confirm(this.$t('fileDetail.confirmDeleteAllRgb'))) {
          await this.clearAllFiles({
            reportId,
            resourceType
          });
          this.initImages(ResourceTypes.RGB);
        }
        break;
      case ResourceTypes.IR:
        if (confirm(this.$t('fileDetail.confirmDeleteAllIr'))) {
          await this.clearAllFiles({
            reportId,
            resourceType
          });
          this.initImages(ResourceTypes.IR);
        }
        break;
      case ResourceTypes.CSV:
        if (confirm(this.$t('fileDetail.confirmDeleteAllCsv'))) {
          await this.clearAllFiles({
            reportId,
            resourceType
          });
          this.initImages(ResourceTypes.CSV);
        }
        break;
      default:
        break;
      }
    },
    isSelected(resourceType, imageId) {
      console.log(
        '테스트 include:',
        this.localSelectedImages[resourceType].includes(imageId)
      );
      return this.localSelectedImages[resourceType].includes(imageId);
    },
    selectImage(resourceType, image) {
      //선택모드일때는 해당항목선택
      if (this.isSelectMode[resourceType]) {
        const imageKey = this.getKey(resourceType, image);
        this.toggleSelection(resourceType, imageKey);
      } else {
        //선택모드 아닐때는 ㅇ
        this.ImageDialog = true;
        this.ImageDialogItem = image;
      }
    },
    next() {
      this.page++;
      this.initCSVFiles();
    },
    previous() {
      this.page--;
      this.initCSVFiles();
    },
    paginationInput(event) {
      this.page = event;
      this.initCSVFiles();
    },
    async initCSVFiles() {
      const url = '/api/solarThermalFiles/search/byReport';
      try {
        const localPage = this.page - 1;
        this.apiLoading = true;
        const res = await this.init({
          resourceType: this.ResourceTypes.CSV,
          reportId: this.$props.reportId,
          currentPage: localPage
        });

        this.pageLength = Math.ceil(res.data?.page.totalPages);
        this.csvFiles = res.data?._embedded?.solarThermalFiles;
        this.apiLoading = false;
      } catch (e) {
        console.error('테스트 err', e);
      }
    },
    resetData() {
      this.activeTab = 0;
      this.isSelectMode = {
        [ResourceTypes.RGB]: false,
        [ResourceTypes.IR]: false,
        [ResourceTypes.CSV]: true
      };
      this.images = {
        [ResourceTypes.RGB]: [],
        [ResourceTypes.IR]: [],
        [ResourceTypes.CSV]: []
      };
      this.localSelectedImages = {
        [ResourceTypes.RGB]: [],
        [ResourceTypes.IR]: [],
        [ResourceTypes.CSV]: []
      };
      this.checkedRows = [];
      this.allChecked = false;
      this.loading = {
        [ResourceTypes.RGB]: false,
        [ResourceTypes.IR]: false,
        [ResourceTypes.CSV]: false
      };
      this.page = 1;
      this.pageLength = 0;
      this.itemsPerPage = 10;
    },
    async initImages(resourceType) {
      const reportId = this.$props.reportId;
      if (this.loading[resourceType]) return;
      else this.$set(this.loading, resourceType, true);
      try {
        if (resourceType === ResourceTypes.CSV) {
          this.initCSVFiles();
        } else {
          await this.init({ resourceType, reportId });
          this.$set(
            this.images,
            resourceType,
            this.resourceImages(resourceType)
          );
        }
      } catch (error) {
        console.error('이미지 로드 오류:', error);
      } finally {
        this.loading[resourceType] = false;
      }
    },
    /**
     * csv파일은 해당기능없음
     */
    deselectAll(resource) {
      this.localSelectedImages[resource] = [];
    },
    handleImageHover(resource, image) {
      this.hover = this.getKey(resource, image);
      this.$set(this.hoveredFileName, resource, image?.imgName || null);
    },
    handleImageLeave(resource) {
      this.hover = null;
      this.$set(this.hoveredFileName, resource, null);
    },
    handleEsc() {
      console.log('ESC 키가 눌렸습니다.');
      this.closeDialog();
    },
    /**
     * 선택된 이미지 다운로드 (RGB/IR 공통)
     */
    async downloadSelectedImages(resource) {
      try {
        const allImages = this.resourceImages(resource);
        const selectedKeys = this.localSelectedImages[resource];
        const resourceTypeName = resource === ResourceTypes.RGB ? 'RGB' : 'IR';
        
        if (!selectedKeys || selectedKeys.length === 0) {
          alert(this.$t('fileDetail.noSelectedImages'));
          return;
        }
        
        // 선택된 이미지만 필터링
        const selectedImages = allImages.filter(image => {
          const key = this.getKey(resource, image);
          return selectedKeys.includes(key);
        });
        
        if (selectedImages.length === 0) {
          alert(this.$t('fileDetail.selectedImagesNotFound'));
          return;
        }

        // 보고서 정보가 없으면 로드
        if (!this.reportData) {
          await this.loadReportInfo();
        }
        
        if (!this.reportData) {
          alert(this.$t('fileDetail.reportLoadFailed'));
          return;
        }
        
        const folderName = generateFolderNameFromReport(this.reportData);
        const zipBaseName = `${folderName}_${resourceTypeName}_${this.$t('fileDetail.zipSuffixSelected')}`;
        await this.downloadImagesWithSplit(selectedImages, folderName, zipBaseName, resourceTypeName);
      } catch (error) {
        console.error(`${resource === ResourceTypes.RGB ? 'RGB' : 'IR'} 선택 이미지 다운로드 중 오류 발생:`, error);
        alert(this.$t('fileDetail.downloadError'));
      }
    },
    /**
     * 이미지 전체 다운로드 (RGB/IR 공통)
     */
    async downloadAllImages(resource) {
      try {
        const images = this.resourceImages(resource);
        const resourceTypeName = resource === ResourceTypes.RGB ? 'RGB' : 'IR';
        
        if (!images || images.length === 0) {
          alert(this.$t('fileDetail.noImagesToDownload', { type: resourceTypeName }));
          return;
        }

        // 보고서 정보가 없으면 로드
        if (!this.reportData) {
          await this.loadReportInfo();
        }
        
        if (!this.reportData) {
          alert(this.$t('fileDetail.reportLoadFailed'));
          return;
        }
        
        const folderName = generateFolderNameFromReport(this.reportData);
        const zipBaseName = `${folderName}_${resourceTypeName}`;
        await this.downloadImagesWithSplit(images, folderName, zipBaseName, resourceTypeName);
      } catch (error) {
        console.error(`${resource === ResourceTypes.RGB ? 'RGB' : 'IR'} 이미지 다운로드 중 오류 발생:`, error);
        alert(this.$t('fileDetail.downloadError'));
      }
    },
    async downloadImagesWithSplit(images, folderName, zipBaseName, resourceTypeName) {
      const { batches, totalEstimatedBytes } = await splitImagesByMaxBytes(images, MAX_ZIP_DOWNLOAD_BYTES);
      if (!batches.length) {
        alert(this.$t('fileDetail.downloadError'));
        return;
      }

      const estimatedMb = (totalEstimatedBytes / (1024 * 1024)).toFixed(1);
      const zipLimitMb = (MAX_ZIP_DOWNLOAD_BYTES / (1024 * 1024)).toFixed(0);
      if (batches.length > 1) {
        alert(this.$t('fileDetail.downloadSplitNotice', {
          totalMb: estimatedMb,
          limitMb: zipLimitMb,
          parts: batches.length
        }));
      }

      let downloadedCount = 0;
      const errors = [];
      for (let i = 0; i < batches.length; i += 1) {
        const partFileName = batches.length === 1
          ? zipBaseName
          : `${zipBaseName}_part${i + 1}`;
        const result = await downloadImagesAsZip(batches[i], folderName, partFileName);
        if (result.success) {
          downloadedCount += result.downloadedCount || 0;
          if (result.errors?.length) {
            errors.push(...result.errors);
          }
        } else {
          errors.push(result.error || this.$t('fileDetail.downloadError'));
        }
      }

      if (downloadedCount === 0) {
        alert(errors[0] || this.$t('fileDetail.downloadError'));
        return;
      }

      let message = this.$t('fileDetail.downloadSuccess', {
        count: downloadedCount,
        type: resourceTypeName
      });
      if (errors.length > 0) {
        message += `\n\n${this.$t('fileDetail.downloadPartialFailures')}\n${errors.slice(0, 5).join('\n')}`;
        if (errors.length > 5) {
          message += `\n${this.$t('fileDetail.downloadMoreFailures', { count: errors.length - 5 })}`;
        }
      }
      alert(message);
    },
    ellipsisString
  },
  mounted() {
    if (this.$props.dialog == true) {
      this.resourceType = ResourceTypes.RGB;
      if (this.activeTab == 0 && this.images[ResourceTypes.RGB].length == 0) {
        this.initImages(ResourceTypes.RGB);
      }
    }
  }
};
</script>

<style scoped>
.gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 5px;
  height: calc(90vh - 250px);
  /* height: calc(100vh - 310px); */
  align-items: flex-start;
  align-content: flex-start;
  flex-grow: 1;
  overflow-y: auto;
}
.fullGallery {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 156px);
}

.image-container {
  position: relative;
  margin: 10px;
  width: 200px;
  height: 200px;
  cursor: pointer;
  transition: transform var(--solvit-motion-duration) var(--solvit-motion-easing),
    border-color var(--solvit-motion-duration) var(--solvit-motion-easing);
  border: 1px solid var(--v-secondary-base);
}

.image-container:hover {
  border: 1px solid var(--v-text-base);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.file-name {
  position: absolute;
  bottom: 5px;
  left: 5px;
  background-color: rgba(127, 127, 127, 0.4);
  color: var(--v-text-base);
  font-weight: 500;
  padding: 2px 5px;
  border-radius: 3px;
  opacity: 1;
  transition: opacity var(--solvit-motion-duration) var(--solvit-motion-easing);
  max-width: calc(100% - 10px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-name.hovered {
  opacity: 0;
}


.image-container img {
  padding: 3px;
  width: 100%;
  height: 100%;
}

.checkbox {
  position: absolute;
  top: 5px;
  left: 5px;
}

.delete-button {
  position: absolute;
  top: 5px;
  right: 5px;
  display: none;
}

.image-container:hover .delete-button {
  display: block;
}
.image-container:hover .showIcon_hover_selected {
  display: block;
  background-image: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--v-text-base) 80%, transparent),
    color-mix(in srgb, var(--v-text-base) 40%, transparent)
  );
}
.showIcon_hover_selected:not(:hover) {
  display: none;
}
.dynamic_height {
  height: calc(90vh - 156px);
}

.hovered-file-name {
  background-color: color-mix(in srgb, var(--v-text-base) 35%, var(--v-surface-base));
  color: var(--v-text-base);
}

.image-preview-dialog-card {
  background-color: var(--v-background-base);
}
</style>
